/* eslint-disable react/prop-types */
// eslint-disable-next-line react/prop-types
import { Card, CardContent, Typography, Box, Button } from '@mui/material';
import coins from '../../../assets/coinss.svg';
import { PricingPlan } from '../../../types/Pricing';
import useAuthToken from '../../../hooks/useAuthToken';
import { signInWithRedirect } from 'aws-amplify/auth';
import { stripRedirect } from '../../../hooks/useNavigation';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { useEffect } from 'react';
import { setProfileDetails } from '../../../store/actions/profileActions';
import { Subscription } from '../../../types/Subscription';
import { useNavigate } from 'react-router-dom';
import storageIcon from '../../../assets/storageIcon.png';

export function renderIcon(feature: string) {
  const lowerFeature = feature.toLowerCase();
  if (lowerFeature.includes('storage')) {
    return <img src={storageIcon} alt="Storage icon" style={{ width: 16, height: 16 }} />;
  } else if (lowerFeature.includes('credits')) {
    return <img src={coins} alt='coin' style={{ width: 21, height: 16 }} />;
  } else {
    return null;
  }
}

export default function PricingCard({ 
  plan, 
  subscription, 
  pricingIndex, 
  redirectFreeUserToBilling
}: { 
  plan: PricingPlan, 
  subscription: Subscription, 
  pricingIndex: number, 
  redirectFreeUserToBilling?: boolean
}) {

  const { profile } = useAppSelector(e => e.profile);
  const { isAuthenticated, isLoading } = useAuthToken();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const interval = {
    'year': '/year',
    'month': '/month'
  };

  const planSubtitle = {
    trial: 'Experience Saagas',
    light: 'Discover the Possibilities',
    pro: 'Best for frequent creators',
    business: 'Includes priority support'
  };

  // Only highlight the Pro plan
  const shouldHighlight = plan?.name?.toLowerCase()?.includes('pro');

  const handleClick = async () => {

    if (typeof window === 'undefined') return;

    if (!isAuthenticated && !isLoading) {
      await signInWithRedirect({ provider: 'Google' });
    }

    if (redirectFreeUserToBilling) {
      localStorage.removeItem('planId');
      navigate('/account#billing');
    }

    const planId = plan?.prices?.[pricingIndex]?.priceId;

    localStorage.setItem('planId', planId);
    const nonEmptyKeys = profile ? Object?.entries(profile)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value && Object?.keys(value)?.length > 0)
      ?.map(([key]) => key) : [];

    if (isAuthenticated && !isLoading) {
      if (nonEmptyKeys?.length != 0) {
        stripRedirect(subscription);
        return;
      } else {
        return window.location.href = '/sign-in';
      }
    }

  };

  useEffect(() => {
    if (isAuthenticated && !profile) {
      dispatch(setProfileDetails());
    }
  }, [dispatch, isAuthenticated, profile]);

  return (
    <Card
      variant="outlined"
      sx={{
        width: { xs: '90%', md: '20%' },
        position: 'relative',
        mx: { xs: 'auto', md: 0 },
        borderRadius: '1rem',
        border: '1px solid',
        borderColor: shouldHighlight ? 'transparent' : '#393939',
        backgroundColor: '#18181B',
        color: '#fff',
        transition: 'transform 0.3s',
        '&:hover': { transform: 'scale(1.05)' },
        // Add the gradient border effect when highlighted
        ...(shouldHighlight && {
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '1rem',
            padding: '1px',
            background: 'linear-gradient(90deg, #53ADC5 0%, #EA52A1 100%)',
            WebkitMask:
              'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none',
            zIndex: 1,
          },
          zIndex: 0,
        }),
      }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: .5 }}>
        <Box display="flex"  alignItems="center" gap={1}>
          <Typography variant="h6" sx={{ fontSize: '16px' }} gutterBottom>
            {plan.name}
          </Typography>
          {plan?.name?.toLowerCase()?.includes('pro') && (
            <Typography
              variant="caption"
              sx={{
                color: '#0CAECF',
                fontWeight: 600,
                fontSize: '0.8rem',
                mt: {
                  md:'-3px',
                }
              }}
            >
              Recommended
            </Typography>
          )}
        </Box>

        <Typography variant="body2" sx={{ fontSize: '14px', gap: '16px' }} gutterBottom>
          {planSubtitle[plan?.name?.toLowerCase()]}
        </Typography>

        <Typography variant="body2" sx={{ fontSize: '0.7rem' }} gutterBottom>
          {plan.description}
        </Typography>

        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '2rem' }} mb={2}>
          ${plan.prices?.[pricingIndex]?.amount} {' '}
          <span style={{ fontSize: 16, fontWeight: '600' }}>{interval[plan?.prices?.[pricingIndex]?.interval]}</span>
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: '0.5rem' }}>
          {plan.features.map((feature, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
              {renderIcon(feature)}
              <Typography variant="body1" color="#A8A8A8" sx={{ fontSize: '14px', fontWeight: 600 }}>
                {feature}
              </Typography>
            </Box>
          ))}


   

        </Box>
        <Box mt={2}>
          <Button
            onClick={handleClick}
            variant="contained"
            data-cy={`plan-option-${plan?.name?.toLowerCase()}`}
            sx={{
              background: 'linear-gradient(173deg, #0CAECF -29.1%, #580F6B 96.45%)',
              paddingY: 1.5,
              paddingX: 3,
              fontSize: '14px',
              fontWeight: 600,
              height: '32px',
              width: '100%',
              textTransform: 'none',
              borderRadius: '25px',
              color: 'white',
            }}
          >
            {plan?.name?.toLowerCase()?.includes('trial') ? "Try" : "Subscribe"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}